import { faDatabase, faUpload } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Alert, AutoComplete, Button, Checkbox, Form, Input, Modal, Radio, Segmented, Select, Space, Splitter } from "antd";
import type { FormInstance } from "antd/es/form";
import FormItem from "antd/es/form/FormItem";
import type { RcFile } from "antd/es/upload";
import businessAreaDataDomains from "common/data/businessAreaDataDomains";
import { descriptionRules, nameRules } from "common/FormValidationRules";
import mergeClassNames from "common/mergeClassNames";
import FlexContainerLayout from "components/atoms/FlexContainerLayout";
import DomainDataEditTable, { DomainDataChanges, DomainDataCreateValues, DomainDataMutationResult } from "components/molecules/DomainDataEditTable";
import DomainDataPreviewList from "components/molecules/DomainDataPreviewList";
import FileDragger from "components/molecules/FileDragger";
import type { IFileDraggerReadResult } from "components/molecules/FileDragger/types";
import DataTagSelect from "containers/DataTagSelect";
import React, { Component } from "react";
import CypressTestIds from "../../../cypress/CypressTestIds";

const { Option } = Select;

type ManageBusinessAreaDataAction = "view" | "edit" | "create";

type UploadType = "REPLACE_DATA" | "APPEND_DATA";

type ManageBusinessAreaDataFormData = Partial<IBusinessAreaData> & {
    businessAreaId?: string;
    uploadFile?: RcFile | null;
    dataUploadOptions?: {
        uploadType?: UploadType;
        firstRowHeader?: boolean;
    };
};

type BusinessAreaDomainDataResult = {
    error?: boolean;
    inProgress?: boolean;
    domainDataResult?: {
        Items: IDomainDataResultItem[];
        pageKey?: string;
    };
};

type ManageBusinessAreaDataProps = {
    action: ManageBusinessAreaDataAction;
    businessAreaData?: ManageBusinessAreaDataFormData;
    businessAreaList: IBusinessArea[];
    businessAreaDomainDataResult?: BusinessAreaDomainDataResult;
    createBusinessAreaData?: (businessAreaData: ManageBusinessAreaDataFormData, dataSegment: "manual" | "upload") => void;
    updateBusinessAreaData?: (previousBusinessAreaData: ManageBusinessAreaDataFormData | undefined, businessAreaData: ManageBusinessAreaDataFormData) => void;
    onCancel: () => void;
    onValuesChanged?: (changedValues: Partial<ManageBusinessAreaDataFormData>, values: ManageBusinessAreaDataFormData) => void;
    onFetchNextBusinessAreaDomainData?: (dataDomainId: string, pageKey: string) => void;
    onAddDomainData?: (values: DomainDataCreateValues) => void | Promise<void>;
    onEditDomainData?: (record: IDomainDataResultItem, values: DomainDataChanges) => void | Promise<void>;
    onDeleteDomainData?: (record: IDomainDataResultItem) => void | Promise<void>;
    domainDataMutationResult?: DomainDataMutationResult;
    formErrors?: Record<string, string>;
    permission: {
        canAdd: boolean;
        canEdit: boolean;
        canDelete?: boolean;
    };
};

type ManageBusinessAreaDataState = {
    readOnlyView: boolean;
    uploadFileExist: boolean;
    formDirty: boolean;
    selectedDataSegment: "manual" | "upload";
};

type FormErrorProps = {
    help: string;
    validateStatus: "error";
};

type ChangedField = {
    touched?: boolean;
    name?: (string | number)[];
};

class ManageBusinessAreaData extends Component<ManageBusinessAreaDataProps, ManageBusinessAreaDataState> {
    private businessAreaDataDefault: ManageBusinessAreaDataFormData;
    private formRef: React.RefObject<FormInstance<ManageBusinessAreaDataFormData>>;
    private fileDraggerRef: React.RefObject<IFileDraggerRef>;
    private emptyObject: {};

    constructor(props: ManageBusinessAreaDataProps) {
        super(props);
        this.businessAreaDataDefault = {
            usedForTagging: false,
        };
        this.formRef = React.createRef();
        this.fileDraggerRef = React.createRef();
        this.state = {
            readOnlyView: this.props.action === "view",
            uploadFileExist: false,
            formDirty: false,
            selectedDataSegment: this.props.action === "create" ? "upload" : "manual",
        };
        this.emptyObject = {};
    }

    componentDidMount() {
        if (this.props.businessAreaData) {
            this.formRef.current?.setFieldsValue({ ...this.props.businessAreaData });
        } else {
            this.selectDefaultBusinessArea();
        }
    }

    componentDidUpdate(prevProps: ManageBusinessAreaDataProps) {
        if (this.props.businessAreaList !== prevProps.businessAreaList && this.props.businessAreaList.length > 0) {
            this.selectDefaultBusinessArea();
        }
    }

    selectDefaultBusinessArea = () => {
        if (this.props.businessAreaList.length > 0) {
            if (this.formRef.current) {
                let selectedBusinessAreaId = this.formRef.current.getFieldValue(["businessAreaId"]);
                if (!selectedBusinessAreaId) {
                    this.formRef.current.setFieldsValue({ businessAreaId: this.props.businessAreaList[0].businessAreaId });
                }
            }
        }
    };

    onFormSubmit = (formData: ManageBusinessAreaDataFormData) => {
        if (!this.isSaveAllowed()) {
            return;
        }
        if (this.props.action === "edit") {
            this.props.updateBusinessAreaData?.(this.props.businessAreaData, { ...formData });
        } else {
            let businessAreaData = { ...this.businessAreaDataDefault, ...formData };
            this.props.createBusinessAreaData?.(businessAreaData, this.state.selectedDataSegment);
        }
    };

    getFormErrors = (fieldName: string): FormErrorProps | {} => {
        if (this.props.formErrors && this.props.formErrors[fieldName]) {
            return {
                help: this.props.formErrors[fieldName],
                validateStatus: "error",
            };
        }
        return this.emptyObject;
    };

    isReadOnlyView = () => {
        return this.state.readOnlyView || !(this.props.permission.canEdit || this.props.permission.canAdd);
    };

    isSaveAllowed = () => {
        if (this.props.action === "edit") {
            return this.props.permission.canEdit;
        } else if (this.props.action === "create") {
            return this.props.permission.canAdd;
        }
        return false;
    };

    readFileComplete = (fileReadResult: IFileDraggerReadResult) => {
        this.formRef.current?.setFields([
            {
                name: "uploadFile",
                value: fileReadResult.file,
            },
        ]);
        this.setState({
            uploadFileExist: true,
            formDirty: true,
        });
    };

    readFileError = () => {};

    cancelUpload = () => {
        this.formRef.current?.setFields([
            {
                name: "uploadFile",
                value: null,
            },
        ]);
        this.setState({
            uploadFileExist: false,
            formDirty: this.formRef.current?.isFieldsTouched() ?? false,
        });
    };

    uploadFileExist = () => {
        if (this.formRef.current) {
            let uploadFile = this.formRef.current.getFieldValue(["uploadFile"]);
            if (uploadFile) {
                return true;
            }
        }
        return false;
    };

    onFieldsChange = (fields: ChangedField[]) => {
        if (this.formRef.current) {
            let formDirty = this.formRef.current.isFieldsTouched();
            if (fields && fields.length > 0) {
                for (let field of fields) {
                    if (field.touched && field.name && field.name.length > 0) {
                        if (field.name[0] === "businessAreaId") {
                            this.formRef.current.setFields([
                                {
                                    name: "classifiers",
                                    value: [],
                                },
                            ]);
                        }
                    }
                }
            }
            let uploadFile = this.formRef.current.getFieldValue(["uploadFile"]);
            if (uploadFile) {
                this.setState({
                    uploadFileExist: true,
                    formDirty,
                });
            } else if (this.state.formDirty !== formDirty) {
                this.setState({
                    formDirty,
                });
            }
        }
    };

    onDomainDataPageSelect = (pagination: IPagination) => {
        if (
            this.props.onFetchNextBusinessAreaDomainData &&
            this.props.businessAreaData?.dataDomainId &&
            this.props.businessAreaDomainDataResult?.domainDataResult?.pageKey &&
            pagination.current * pagination.pageSize >= this.props.businessAreaDomainDataResult.domainDataResult.Items.length
        ) {
            this.props.onFetchNextBusinessAreaDomainData(this.props.businessAreaData.dataDomainId, this.props.businessAreaDomainDataResult.domainDataResult.pageKey);
        }
    };

    selectDataSegment = (value: "manual" | "upload") => {
        if (value === "manual" && this.state.uploadFileExist) {
            Modal.confirm({
                title: "Discard the selected CSV file?",
                content: "Switching to manual editing removes the CSV currently selected for upload.",
                okText: "Discard file",
                okButtonProps: { danger: true },
                onOk: () => {
                    this.fileDraggerRef.current?.clearFile();
                    this.cancelUpload();
                    this.setState({ selectedDataSegment: "manual" });
                },
            });
            return;
        }
        this.setState({ selectedDataSegment: value });
    };

    render() {
        let businessAreaOptions = this.props.businessAreaList.map((businessArea) => ({ id: businessArea.businessAreaId, name: businessArea.name }));
        let dataDomains = businessAreaDataDomains.map((domain) => ({ value: domain }));
        return (
            <Form
                layout="vertical"
                labelAlign="left"
                ref={this.formRef}
                name="basic"
                onFinish={this.onFormSubmit}
                initialValues={this.businessAreaDataDefault}
                onFieldsChange={this.onFieldsChange}
                onValuesChange={this.props.onValuesChanged}
                className="flex size-full flex-1 flex-col"
            >
                <FlexContainerLayout
                    footerClassName="justify-between items-center"
                    footer={
                        this.props.action === "view" ? (
                            <div className="flex w-full items-center justify-end gap-4">
                                <Button type="primary" onClick={this.props.onCancel} data-testid={CypressTestIds.MANAGE_BUSINESS_AREA_DATA_CANCEL_BUTTON}>
                                    Back To List
                                </Button>
                            </div>
                        ) : (
                            <div className="flex w-full items-center justify-end gap-4">
                                {this.state.selectedDataSegment === "upload" && (
                                    <div className="flex flex-1 items-center gap-4 rounded-md bg-slate-50 px-4 *:mb-0">
                                        <Form.Item name={["dataUploadOptions", "firstRowHeader"]} valuePropName="checked" initialValue={true}>
                                            <Checkbox
                                                disabled={this.isReadOnlyView() || !this.state.uploadFileExist}
                                                data-testid={CypressTestIds.MANAGE_BUSINESS_AREA_DATA_FIRST_ROW_IS_HEADER_CHECKBOX}
                                            >
                                                First row is header
                                            </Checkbox>
                                        </Form.Item>
                                        {this.props.action !== "create" && (
                                            <Form.Item name={["dataUploadOptions", "uploadType"]} initialValue={"REPLACE_DATA"} className="pt-[5px]">
                                                <Radio.Group disabled={this.isReadOnlyView() || !this.state.uploadFileExist}>
                                                    <Space orientation="horizontal">
                                                        <Radio value="REPLACE_DATA" data-testid={CypressTestIds.MANAGE_BUSINESS_AREA_DATA_REPLACE_DATA_OPTION}>
                                                            Replace existing data in the dataset
                                                        </Radio>
                                                        <Radio value="APPEND_DATA" data-testid={CypressTestIds.MANAGE_BUSINESS_AREA_DATA_APPEND_DATA_OPTION}>
                                                            Append this data to the existing
                                                        </Radio>
                                                    </Space>
                                                </Radio.Group>
                                            </Form.Item>
                                        )}
                                    </div>
                                )}
                                <div className="flex items-center justify-end gap-4">
                                    <Button onClick={this.props.onCancel} data-testid={CypressTestIds.MANAGE_BUSINESS_AREA_DATA_CANCEL_BUTTON}>
                                        {this.isReadOnlyView() || !this.isSaveAllowed() || !this.state.formDirty ? "Back to List" : "Cancel"}
                                    </Button>
                                    <Button
                                        type="primary"
                                        htmlType="submit"
                                        disabled={this.isReadOnlyView() || !this.isSaveAllowed() || !this.state.formDirty}
                                        data-testid={CypressTestIds.MANAGE_BUSINESS_AREA_DATA_SAVE_BUTTON}
                                    >
                                        {this.state.uploadFileExist ? "Save and upload" : "Save"}
                                    </Button>
                                </div>
                            </div>
                        )
                    }
                >
                    <Splitter>
                        <Splitter.Panel defaultSize={400} min={200} max={800} className="overflow-hidden">
                            <div className="scroll-webkit flex size-full flex-1 flex-col gap-4 overflow-auto pr-3 *:mb-0">
                                <Form.Item className="wrap-label" {...this.getFormErrors("businessAreaId")} label="Business Area" name={["businessAreaId"]}>
                                    <Select
                                        {...(() => (this.isReadOnlyView() ? { open: false } : {}))()}
                                        data-testid={CypressTestIds.MANAGE_BUSINESS_AREA_DATA_BUSINESS_AREA_NAME_SELECT}
                                    >
                                        {businessAreaOptions.map((item) => (
                                            <Option key={item.id || Date.now()} value={item.id}>
                                                {item.name}
                                            </Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                                <Form.Item
                                    className="wrap-label"
                                    {...this.getFormErrors("domainName")}
                                    label="Domain Name"
                                    name={["domainName"]}
                                    validateFirst={true}
                                    rules={nameRules}
                                >
                                    <AutoComplete
                                        disabled={this.isReadOnlyView()}
                                        options={dataDomains}
                                        showSearch={{
                                            filterOption: (inputValue, option) => option?.value?.toUpperCase().indexOf(inputValue.toUpperCase()) !== -1,
                                        }}
                                        data-testid={CypressTestIds.MANAGE_BUSINESS_AREA_DATA_DOMAIN_NAME_INPUT}
                                    />
                                </Form.Item>
                                <Form.Item {...this.getFormErrors("domainDescription")} label="Description" name={["domainDescription"]} rules={descriptionRules}>
                                    <Input readOnly={this.isReadOnlyView()} data-testid={CypressTestIds.MANAGE_BUSINESS_AREA_DATA_DESCRIPTION_INPUT} />
                                </Form.Item>
                                <Form.Item {...this.getFormErrors("domainPurpose")} label="Purpose" name={["domainPurpose"]} rules={descriptionRules}>
                                    <Input readOnly={this.isReadOnlyView()} data-testid={CypressTestIds.MANAGE_BUSINESS_AREA_DATA_PURPOSE_INPUT} />
                                </Form.Item>
                                <FormItem dependencies={["businessAreaId"]} label="Data Tags">
                                    {({ getFieldValue }) => {
                                        return (
                                            <Form.Item {...this.getFormErrors("classifiers")} name={["classifiers"]} className="mb-0">
                                                <DataTagSelect
                                                    businessAreaId={getFieldValue(["businessAreaId"])}
                                                    disabled={this.isReadOnlyView()}
                                                    mode="multiple"
                                                    placeholder="Please select classifiers"
                                                    data-testid={CypressTestIds.MANAGE_BUSINESS_AREA_DATA_DATA_TAGS_SELECT}
                                                ></DataTagSelect>
                                            </Form.Item>
                                        );
                                    }}
                                </FormItem>
                            </div>
                        </Splitter.Panel>
                        <Splitter.Panel min={200}>
                            <div className="flex size-full flex-1 flex-col gap-3 overflow-hidden px-3">
                                <Segmented
                                    options={[
                                        {
                                            label: "Edit Records",
                                            value: "manual",
                                            icon: <FontAwesomeIcon icon={faDatabase} />,
                                        },
                                        {
                                            label: "Upload CSV",
                                            value: "upload",
                                            icon: <FontAwesomeIcon icon={faUpload} />,
                                        },
                                    ]}
                                    block={!this.isReadOnlyView() && this.props.action !== "view"}
                                    value={this.state.selectedDataSegment}
                                    onChange={(value) => this.selectDataSegment(value as "manual" | "upload")}
                                    className={this.isReadOnlyView() || this.props.action === "view" ? "hidden" : ""}
                                />
                                <div
                                    className={mergeClassNames(
                                        "size-full flex-1 flex-col gap-4",
                                        this.isReadOnlyView() || this.state.selectedDataSegment !== "upload" ? "hidden" : "flex",
                                    )}
                                >
                                    <Form.Item name={["uploadFile"]} valuePropName="file" noStyle>
                                        <FileDragger
                                            ref={this.fileDraggerRef}
                                            disabled={this.isReadOnlyView()}
                                            onReadFileComplete={this.readFileComplete}
                                            onReadFileError={this.readFileError}
                                            onCancelUpload={this.cancelUpload}
                                            enableColumnMapping={true}
                                        ></FileDragger>
                                    </Form.Item>
                                </div>
                                {this.props.action === "edit" && this.state.selectedDataSegment === "manual" && (
                                    <div className="flex size-full flex-1 flex-col overflow-hidden">
                                        <DomainDataEditTable
                                            dataDomainId={this.props.businessAreaData?.dataDomainId}
                                            items={this.props.businessAreaDomainDataResult?.domainDataResult?.Items ?? []}
                                            recordCount={this.props.businessAreaData?.recordCount ?? 0}
                                            loading={!this.props.businessAreaDomainDataResult || this.props.businessAreaDomainDataResult.inProgress}
                                            permission={{
                                                canAdd: this.props.permission.canAdd,
                                                canEdit: this.props.permission.canEdit,
                                                canDelete: this.props.permission.canDelete ?? false,
                                            }}
                                            mutationResult={this.props.domainDataMutationResult}
                                            onAdd={this.props.onAddDomainData ?? (() => undefined)}
                                            onEdit={this.props.onEditDomainData ?? (() => undefined)}
                                            onDelete={this.props.onDeleteDomainData ?? (() => undefined)}
                                            onPageSelect={this.onDomainDataPageSelect}
                                        />
                                    </div>
                                )}
                                {this.props.action === "create" && this.state.selectedDataSegment === "manual" && (
                                    <Alert title="Save the business area to add domain data manually, or upload a CSV file to add multiple entries at once." />
                                )}
                                {this.props.action === "view" && (
                                    <div className="flex size-full flex-1 flex-col overflow-hidden">
                                        <DomainDataPreviewList
                                            recordCount={this.props.businessAreaData?.recordCount || 0}
                                            domainDataList={
                                                this.props.businessAreaDomainDataResult && this.props.businessAreaDomainDataResult.domainDataResult
                                                    ? this.props.businessAreaDomainDataResult.domainDataResult.Items
                                                    : []
                                            }
                                            loading={
                                                !this.props.businessAreaDomainDataResult || this.props.businessAreaDomainDataResult.inProgress
                                                    ? { description: "Loading data..." }
                                                    : false
                                            }
                                            onPageSelect={this.onDomainDataPageSelect}
                                            data-testid={CypressTestIds.MANAGE_BUSINESS_AREA_DATA_DOMAIN_DATA_PREVIEW_LIST}
                                        ></DomainDataPreviewList>
                                    </div>
                                )}
                            </div>
                        </Splitter.Panel>
                    </Splitter>
                </FlexContainerLayout>
            </Form>
        );
    }
}

export default ManageBusinessAreaData;
